import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OnboardingDialogsContext, Page } from "contexts/OnboardingDialogs";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { useContext, useEffect, useState } from "react";
import { OrganizationNiche, TemplateError } from "schemas/dashboard";

import { Template } from "features/Influencer/ContactList/schema";
import CreateTemplateDialog from "features/Influencer/HomePage/CreateTemplateDialog";
import EditNicheTemplate from "features/Influencer/VA/VASettings/EditNicheDrawer/EditNicheTemplate";
import { fetcherAuth } from "utils/api";
import { trackEvent } from "utils/tracking";

interface Props {
  open: boolean;
  onClose: () => void;
  onContinue: () => void;
  activeNiches: OrganizationNiche[];
  setUseOneTemplates: (value: boolean) => void;
}

enum DialogStep {
  Warning = "warning",
  NicheSelection = "niche_selection",
  TemplateSelection = "template_selection",
}

const TemplateDiversityWarningDialog = ({
  open,
  onClose,
  onContinue,
  activeNiches,
  setUseOneTemplates,
}: Props) => {
  const { getToken } = useAuth();
  const { setAlert, setErrorAlert } = useContext(AlertContext);
  const { currentOrg, setOrganizationVaProfile, emailSettings } = useContext(
    OrganizationUserContext,
  );
  const { templates: allTemplates, outreachTemplates } = useContext(
    OutreachTemplatesContext,
  );
  const {
    setCreateTemplateOnboardingDialogOpen,
    createTemplateOnboardingDialogOpen,
    setPage,
  } = useContext(OnboardingDialogsContext);

  const [dialogStep, setDialogStep] = useState<DialogStep>(DialogStep.Warning);
  const [selectedNicheIds, setSelectedNicheIds] = useState<number[]>([]);
  const [modifiedTemplates, setModifiedTemplates] = useState<Template[]>([]);
  const [templatesError, setTemplatesError] = useState<TemplateError | null>(
    null,
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [initialTemplateCount, setInitialTemplateCount] = useState<number>(0);
  const [needsTemplateCreation, setNeedsTemplateCreation] =
    useState<boolean>(false);

  const hasMultipleTemplates =
    outreachTemplates && outreachTemplates.length > 1;

  const handleCustomizeTemplates = () => {
    trackEvent(
      "VA Onboarding - Template Diversity Warning - Customize Clicked",
    );
    setNeedsTemplateCreation(false);
    setDialogStep(DialogStep.NicheSelection);
  };

  const handleCreateNewTemplate = () => {
    trackEvent(
      "VA Onboarding - Template Diversity Warning - Create New Template Clicked",
    );
    setNeedsTemplateCreation(true);
    setDialogStep(DialogStep.NicheSelection);
  };

  const handleCreateTemplate = () => {
    setInitialTemplateCount(outreachTemplates?.length || 0);
    setPage(Page.pitchType);
    setCreateTemplateOnboardingDialogOpen(true);
  };

  const toggleNicheSelection = (nicheId: number) => {
    setSelectedNicheIds((prev) =>
      prev.includes(nicheId)
        ? prev.filter((id) => id !== nicheId)
        : [...prev, nicheId],
    );
  };

  const handleContinueAnyway = () => {
    trackEvent("VA Onboarding - Template Diversity Warning - Continue Anyway");
    onContinue();
  };

  const handleBackToWarning = () => {
    setDialogStep(DialogStep.Warning);
    setSelectedNicheIds([]);
  };

  const handleBackToNicheSelection = () => {
    setDialogStep(DialogStep.NicheSelection);
    setModifiedTemplates([]);
    setTemplatesError(null);
  };

  const handleNextToTemplateSelection = () => {
    trackEvent("VA Onboarding - Template Diversity Warning - Niches Selected", {
      nicheCount: selectedNicheIds.length,
    });
    if (needsTemplateCreation) {
      // User needs to create a template first
      handleCreateTemplate();
    } else {
      setDialogStep(DialogStep.TemplateSelection);
    }
  };

  const handleSaveTemplates = async () => {
    if (selectedNicheIds.length === 0 || modifiedTemplates.length === 0) {
      return;
    }

    setLoading(true);
    try {
      for (const nicheId of selectedNicheIds) {
        const res = await fetcherAuth(
          getToken,
          `/api/organization/${currentOrg?.id}/organization-niches/${nicheId}/with-automations`,
          "PUT",
          {},
          {
            templateIds: modifiedTemplates.map((template) => template.id),
          },
          true,
        );
        const jsonMessage = await res.json();

        if (!res.ok && jsonMessage.field) {
          if (
            jsonMessage.field === "templates" &&
            jsonMessage.templateIdx !== undefined &&
            jsonMessage.templateIdx !== null
          ) {
            setTemplatesError({
              idx: jsonMessage.templateIdx,
              message: jsonMessage.message,
            });
          }
          return;
        }
      }

      setOrganizationVaProfile((prevProfile) => {
        if (!prevProfile || !prevProfile?.niches) return prevProfile;
        const updatedNiches = prevProfile.niches.map((niche) => {
          if (niche.id && selectedNicheIds.includes(niche.id)) {
            return {
              ...niche,
              templates: modifiedTemplates,
            };
          }
          return niche;
        });
        return {
          ...prevProfile,
          niches: updatedNiches,
        };
      });

      const selectedNicheNames = activeNiches
        .filter((n) => n.id && selectedNicheIds.includes(n.id))
        .map((n) => n.name)
        .join(", ");

      setAlert(
        `Successfully updated templates for ${selectedNicheNames}`,
        "success",
      );
      trackEvent(
        "VA Onboarding - Template Diversity Warning - Templates Saved",
        {
          nicheCount: selectedNicheIds.length,
        },
      );

      // Switch to "Customize per niche" mode and close
      setUseOneTemplates(false);
      resetAndClose();
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  const resetAndClose = () => {
    setDialogStep(DialogStep.Warning);
    setSelectedNicheIds([]);
    setModifiedTemplates([]);
    setTemplatesError(null);
    onClose();
  };

  // Watch for template creation completion - go to template selection after
  useEffect(() => {
    if (
      !createTemplateOnboardingDialogOpen &&
      outreachTemplates &&
      outreachTemplates.length > initialTemplateCount &&
      initialTemplateCount > 0
    ) {
      // Set the newly created template as the selected template
      const newTemplate = outreachTemplates[outreachTemplates.length - 1];

      let existingFollowUps: Template[] = [];

      const presetTemplates = emailSettings?.emailSettingTemplates || [];
      const matchedTemplates: Template[] = [];

      for (const settingTemplate of presetTemplates) {
        const template = allTemplates?.find(
          (x) => x.id === settingTemplate.templateId,
        );
        if (template) {
          matchedTemplates.push(template);
        }
      }
      if (matchedTemplates?.length > 1) {
        existingFollowUps = matchedTemplates?.slice(
          1,
          matchedTemplates?.length,
        );
      }

      setModifiedTemplates([newTemplate, ...existingFollowUps]);
      setNeedsTemplateCreation(false);
      setDialogStep(DialogStep.TemplateSelection);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createTemplateOnboardingDialogOpen, outreachTemplates?.length]);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setDialogStep(DialogStep.Warning);
      setSelectedNicheIds([]);
      setModifiedTemplates([]);
      setTemplatesError(null);
      setNeedsTemplateCreation(false);
    }
  }, [open]);

  const renderWarningStep = () => (
    <>
      <DialogTitle sx={{ fontSize: 22, pb: 1 }}>
        <Box
          component="i"
          className="fa-solid fa-triangle-exclamation"
          sx={{ color: "warning.main", mr: 1.5 }}
        />
        Consider Using Multiple Templates
      </DialogTitle>
      <DialogContent>
        <Typography paragraph>
          Users with multiple templates tailored to different niches perform
          significantly better in their outreach.
        </Typography>
        <Typography paragraph sx={{ mb: 0 }}>
          Using the same template for all niches may increase the risk of your
          emails being flagged as spam, since personalized emails tend to have
          higher engagement rates.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button color="secondary" onClick={handleContinueAnyway}>
          Continue Anyway
        </Button>
        {hasMultipleTemplates ? (
          <Button
            variant="contained"
            disableElevation
            onClick={handleCustomizeTemplates}
          >
            Add a New Template
          </Button>
        ) : (
          <Button
            variant="contained"
            disableElevation
            onClick={handleCreateNewTemplate}
          >
            Create New Template
          </Button>
        )}
      </DialogActions>
    </>
  );

  const renderNicheSelectionStep = () => (
    <>
      <DialogTitle sx={{ fontSize: 22, pb: 1 }}>
        <Box
          component="i"
          className="fa-solid fa-plus-circle"
          sx={{ color: "primary.main", mr: 1.5 }}
        />
        Add Another Template
      </DialogTitle>
      <DialogContent>
        <Typography paragraph>
          Select which niches you'd like this new template to apply to:
        </Typography>
        <Grid container gap={1} sx={{ mb: 2 }}>
          {activeNiches
            .filter((niche) => niche.id !== undefined)
            .map((niche) => (
              <Chip
                key={niche.id}
                label={niche.name}
                onClick={() => toggleNicheSelection(niche.id!)}
                color={
                  selectedNicheIds.includes(niche.id!) ? "primary" : "default"
                }
                variant={
                  selectedNicheIds.includes(niche.id!) ? "filled" : "outlined"
                }
                sx={{ cursor: "pointer" }}
              />
            ))}
        </Grid>
        {selectedNicheIds.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Click on niches above to select them
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, justifyContent: "space-between" }}>
        <Button color="secondary" onClick={handleBackToWarning}>
          Back
        </Button>
        <Button
          variant="contained"
          disableElevation
          onClick={handleNextToTemplateSelection}
          disabled={selectedNicheIds.length === 0}
        >
          Next
        </Button>
      </DialogActions>
    </>
  );

  const selectedNicheNames = activeNiches
    .filter((n) => n.id && selectedNicheIds.includes(n.id))
    .map((n) => n.name)
    .join(", ");

  const renderTemplateSelectionStep = () => (
    <>
      <DialogTitle sx={{ fontSize: 22, pb: 1 }}>
        <Box
          component="i"
          className="fa-solid fa-envelope"
          sx={{ color: "primary.main", mr: 1.5 }}
        />
        Set Template for Selected Niches
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Applying to: {selectedNicheNames}
        </Typography>
        <EditNicheTemplate
          modifiedTemplates={modifiedTemplates}
          setModifiedTemplates={setModifiedTemplates}
          templatesError={templatesError}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, justifyContent: "space-between" }}>
        <Button color="secondary" onClick={handleBackToNicheSelection}>
          Back
        </Button>
        <LoadingButton
          variant="contained"
          disableElevation
          onClick={handleSaveTemplates}
          loading={loading}
          disabled={modifiedTemplates.length === 0}
        >
          Save & Close
        </LoadingButton>
      </DialogActions>
    </>
  );

  return (
    <>
      <Dialog
        open={open && !createTemplateOnboardingDialogOpen}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
      >
        {dialogStep === DialogStep.Warning && renderWarningStep()}
        {dialogStep === DialogStep.NicheSelection && renderNicheSelectionStep()}
        {dialogStep === DialogStep.TemplateSelection &&
          renderTemplateSelectionStep()}
      </Dialog>

      {createTemplateOnboardingDialogOpen && <CreateTemplateDialog />}
    </>
  );
};

export default TemplateDiversityWarningDialog;
